<template>
  <div class="w-full rounded-lg bg-white p-8 shadow-md">
    <div class="flex w-full flex-col">
      <div
        class="flex w-full flex-col items-center justify-start gap-4 sm:flex-row sm:gap-12"
      >
        <div
          class="flex w-full flex-col items-start justify-start gap-y-2 sm:mr-8 md:w-auto"
        >
          <label class="text-sm text-[#58585E]">{{ __('Product type') }}</label>
          <div class="flex flex-row items-center justify-start gap-x-4">
            <label class="flex cursor-pointer items-center">
              <input
                id="online"
                type="radio"
                name="status"
                value="online"
                :checked="productType === 'online'"
                class="mr-2 border-primary text-primary focus:shadow-none focus:outline-none focus:ring-0 focus:ring-offset-0"
                @click="handleChange('online')"
              />
              {{ __('Online') }}
            </label>

            <!-- Inactive Radio -->
            <label class="flex cursor-pointer items-center">
              <input
                id="offline"
                type="radio"
                name="status"
                value="offline"
                :checked="productType === 'offline'"
                class="mr-2 border-primary text-primary focus:shadow-none focus:outline-none focus:ring-0 focus:ring-offset-0"
                @click="handleChange('offline')"
              />
              {{ __('Offline') }}
            </label>
          </div>
        </div>

        <template v-if="is_logged_in">
          <div class="flex w-full flex-col items-start gap-y-2">
            <!-- All Radio -->

            <!-- Active Radio -->

            <label class="block text-sm text-[#58585E]">{{
              __('Availability')
            }}</label>
            <div class="flex flex-row items-center justify-start gap-x-4">
              <label class="flex cursor-pointer items-center">
                <input
                  id="unlocked"
                  type="radio"
                  name="availability"
                  value="unlocked"
                  :checked="availability === 'unlocked'"
                  class="mr-2 border-primary text-primary focus:shadow-none focus:outline-none focus:ring-0 focus:ring-offset-0"
                  @click="handleChange('unlocked')"
                />
                {{ __('Unlocked') }}
              </label>

              <!-- Inactive Radio -->
              <label class="flex cursor-pointer items-center">
                <input
                  id="locked"
                  type="radio"
                  name="availability"
                  value="locked"
                  :checked="availability === 'locked'"
                  class="mr-2 border-primary text-primary focus:shadow-none focus:outline-none focus:ring-0 focus:ring-offset-0"
                  @click="handleChange('locked')"
                />
                {{ __('Locked') }}
              </label>
            </div>
          </div>
        </template>
      </div>

      <Input
        icon="location_on"
        class="mt-8"
        v-model="locationString"
        :value="locationString"
        :placeholder="__('Location')"
        type="text"
        :maxlength="100"
        aria-label="Location"
      />

      <div
        class="grid grid-cols-1 gap-x-0 gap-y-8 pt-8 sm:grid-cols-3 sm:gap-x-8 sm:gap-y-0"
      >
        <div class="flex flex-col rounded-lg border pb-8 pt-4">
          <div class="flex w-full items-center justify-start border-b">
            <span class="px-8 pb-4 text-lg font-medium">
              {{ __('Points Range') }}</span
            >
          </div>
          <div class="flex h-full flex-col">
            <div class="flex items-center justify-between gap-x-4 px-8 pt-4">
              <Input
                type="number"
                class="w-full"
                :placeholder="__('Min')"
                v-model.number="pointsRange.min"
                :value="pointsRange.min"
                :label="__('Min Points')"
                :min_number="0"
                :max_number="10000"
                @input="updateMinInput"
              />

              <Input
                type="number"
                class="w-full"
                :placeholder="__('Max')"
                v-model.number="pointsRange.max"
                :min_number="pointsRange.min"
                :max_number="10000"
                :value="pointsRange.max"
                :label="__('Max Points')"
                @input="updateMaxInput"
              />
            </div>

            <DualRangeSlider
              v-model="pointsRange"
              :min="0"
              :max="10000"
              :step="100"
              prefix="$"
              class="px-8 pt-10"
            />
          </div>
        </div>

        <div class="flex h-64 flex-col rounded-lg border pb-8 pt-4">
          <div class="flex w-full items-center justify-start border-b">
            <span class="px-8 pb-4 text-lg font-medium">
              {{ __('Product Category') }}</span
            >
          </div>

          <div
            class="scrollbar-thin flex h-full flex-col gap-y-1 overflow-y-auto px-8 pt-4"
          >
            <TransitionGroup
              name="fade-scale"
              tag="div"
              class="contents"
              enter-active-class="transition-all duration-150 ease-out"
              enter-from-class="opacity-0 scale-95"
              enter-to-class="opacity-100 scale-100"
              leave-active-class="transition-all duration-100 ease-in"
              leave-from-class="opacity-100 scale-100"
              leave-to-class="opacity-0 scale-95"
            >
              <div
                class="flex items-center justify-start"
                v-for="category in categories"
                :key="category.id"
              >
                <input
                  type="checkbox"
                  class="size-5 rounded-md border-gray-300 text-primary focus:shadow-none focus:outline-none focus:ring-0 focus:ring-offset-0"
                  :checked="
                    selectedCategories.find(
                      (selectedCategory) => selectedCategory.id === category.id
                    )
                  "
                  @change="toggleCategory(category.id)"
                />
                <label>
                  <span class="ml-2 text-base text-gray-700">{{
                    category.name
                  }}</span>
                </label>
              </div>
            </TransitionGroup>
          </div>
        </div>

        <div class="flex h-64 flex-col rounded-lg border pb-8 pt-4">
          <div class="flex w-full items-center justify-start border-b">
            <span class="px-8 pb-4 text-lg font-medium">
              {{ __('Attributes') }}</span
            >
          </div>

          <div
            class="scrollbar-thin grid grid-cols-2 gap-y-2 overflow-y-auto pt-4 lg:grid-cols-3"
          >
            <TransitionGroup
              name="fade-scale"
              tag="div"
              class="contents"
              enter-active-class="transition-all duration-150 ease-out"
              enter-from-class="opacity-0 scale-95"
              enter-to-class="opacity-100 scale-100"
              leave-active-class="transition-all duration-100 ease-in"
              leave-from-class="opacity-100 scale-100"
              leave-to-class="opacity-0 scale-95"
            >
              <div
                class="flex w-full items-center gap-2 px-8"
                v-for="(value, index) in values"
                :key="index"
              >
                <input
                  type="checkbox"
                  class="size-5 rounded-md border-gray-300 text-primary focus:shadow-none focus:outline-none focus:ring-0 focus:ring-offset-0"
                  :checked="
                    selectedValues.find(
                      (selectedValue) => selectedValue.id === value.id
                    )
                  "
                  @change="toggleValue(value.id)"
                />
                <label>
                  <span class="text-base text-gray-700">{{ value.label }}</span>
                </label>
              </div>
            </TransitionGroup>
          </div>
        </div>
      </div>

      <div
        class="between mt-8 flex w-full flex-col items-center justify-center sm:flex-row sm:justify-between"
      >
        <div
          class="flex w-full items-center justify-center gap-x-2 sm:justify-start"
        >
          <input
            type="checkbox"
            class="size-5 rounded-md border-gray-300 text-primary focus:shadow-none focus:outline-none focus:ring-0 focus:ring-offset-0"
            :checked="doNotShowOutOfStock"
            @change="doNotShowOutOfStock = !doNotShowOutOfStock"
          />
          <span>
            {{ __('Do not showcase products that are out of stock') }}</span
          >
        </div>

        <div
          class="flex w-full flex-row items-center justify-between gap-x-4 pt-8 sm:justify-end sm:pt-0"
        >
          <button
            @click.prevent="resetFilters()"
            class="btn btn-ghost flex items-center justify-center gap-x-2"
          >
            <Icon name="delete" type="outlined"></Icon>
            <span>
              {{ __('Reset Filters') }}
            </span>
          </button>
          <button
            @click.prevent="
              $emit('applyFilters', {
                pointsRange,
                productType,
                availability,
                locationString,
                selectedCategories,
                selectedValues,
                doNotShowOutOfStock
              })
            "
            class="btn btn-fill"
          >
            <span>
              {{ __('Apply Filters') }}
            </span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
  import DualRangeSlider from '@/components/global/DualRangeSlider.vue';
  import Input from '@/components/global/Form/Input.vue';
  import Icon from '@/components/global/Icon.vue';
  import { __ } from '@/helpers';
  import { nextTick, ref, TransitionGroup, watch } from 'vue';

  const props = defineProps({
    products: {
      type: Object,
      required: true
    },
    categories: {
      type: Array,
      required: true
    },
    category_values: {
      type: Array,
      required: true
    },
    is_logged_in: {
      type: Boolean
    }
  });

  const pointsRange = ref({
    min: 0,
    max: 10000
  });
  const productType = ref('');
  const availability = ref('');

  const values = ref(
    props.category_values
      .flatMap((category) =>
        category.attributes.flatMap((attribute) =>
          attribute.values.map((value) => ({
            id: value.id,
            name: value.name,
            label: value.label,
            category_id: value.category_id
          }))
        )
      )
      .filter(
        (item, index, self) =>
          index === self.findIndex((obj) => obj.name === item.name)
      )
  );
  const initialValues = ref(
    props.category_values
      .flatMap((category) =>
        category.attributes.flatMap((attribute) =>
          attribute.values.map((value) => ({
            id: value.id,
            name: value.name,
            label: value.label,
            category_id: value.category_id
          }))
        )
      )
      .filter(
        (item, index, self) =>
          index === self.findIndex((obj) => obj.name === item.name)
      )
  );

  const categories = ref(props.categories);

  const initialCategories = ref(props.categories);
  const updateMinInput = (event) => {
    let value = parseInt(event.target.value) || 0;

    value = Math.max(0, Math.min(10000, value));

    value = Math.min(value, pointsRange.value.max);

    pointsRange.value = {
      ...pointsRange.value,
      min: value
    };
  };

  const updateMaxInput = (event) => {
    let value = parseInt(event.target.value) || 0;

    value = Math.max(0, Math.min(10000, value));

    value = Math.max(value, pointsRange.value.min);

    pointsRange.value = {
      ...pointsRange.value,
      max: value
    };
  };

  const selectedCategories = ref([]);

  const selectedValues = ref([]);

  let isUpdating = false;
  watch(
    [values, selectedValues, categories, selectedCategories],
    () => {
      if (isUpdating) return;

      isUpdating = true;

      filterOptions();

      nextTick(() => {
        isUpdating = false;
      });
    },
    { deep: true }
  );

  const filterOptions = () => {
    if (
      selectedValues.value.length === 0 &&
      selectedCategories.value.length === 0
    ) {
      values.value = initialValues.value;
      categories.value = initialCategories.value;
      return;
    }

    values.value = initialValues.value.filter((initialValue) => {
      if (
        selectedValues.value.length === 0 &&
        selectedCategories.value.length > 0
      ) {
        const allCategoryIds = selectedCategories.value
          .flat()
          .map((item) => item.id);

        return (
          initialValue.category_id === null ||
          allCategoryIds.includes(initialValue.category_id)
        );
      }
      if (
        selectedValues.value.length > 0 &&
        selectedCategories.value.length === 0
      ) {
        if (selectedValues.value.every((value) => value.category_id === null))
          return true;

        return selectedValues.value.some(
          (value) =>
            (value.category_id !== null &&
              value.category_id === initialValue.category_id) ||
            initialValue.category_id === null
        );
      }

      if (
        selectedValues.value.length > 0 &&
        selectedCategories.value.length > 0
      ) {
        // if all selected values have no category_id
        if (selectedValues.value.every((value) => value.category_id === null)) {
          return selectedCategories.value.some(
            (category) =>
              category.id === initialValue.category_id ||
              initialValue.category_id === null
          );
        } else {
          // if some selected values have category_id , keep only the values that match the selected categories and the selected values category id
          return selectedValues.value.some(
            (value) =>
              (value.category_id !== null &&
                value.category_id === initialValue.category_id) ||
              initialValue.category_id === null
          );
        }
      }
    });

    categories.value = initialCategories.value.filter((category) => {
      if (selectedValues.value.every((value) => value.category_id === null))
        return true;
      else
        return selectedValues.value.some(
          (value) => value.category_id === category.id
        );
    });

    // This case handles when we select both categories and then select a value specific to only a category
    // this makes sure to clear all selected categories that we're eliminated in that process
    selectedCategories.value = selectedCategories.value.filter(
      (selectedCategory) =>
        categories.value.some((category) => category.id === selectedCategory.id)
    );
  };

  const areConflicting = (attr1, attr2) => {};

  const toggleCategory = (categoryId) => {
    const category = props.categories.find((item) => item.id === categoryId);

    if (selectedCategories.value.find((item) => item.id === categoryId)) {
      selectedCategories.value = selectedCategories.value.filter(
        (selectedCategory) => selectedCategory.id !== categoryId
      );
    } else {
      selectedCategories.value.push({
        id: categoryId,
        name: category.name
      });
    }
  };

  const doNotShowOutOfStock = ref(false);
  const toggleValue = (valueId) => {
    if (selectedValues.value.find((item) => item.id === valueId)) {
      selectedValues.value = selectedValues.value.filter(
        (selectedValue) => selectedValue.id !== valueId
      );
    } else {
      selectedValues.value.push(
        values.value.find((item) => item.id === valueId)
      );
    }
  };

  const locationString = ref('');

  const handleChange = (value) => {
    if (value === 'online' && productType.value !== 'online') {
      productType.value = 'online';
    } else if (value === 'online' && productType.value === 'online') {
      productType.value = '';
    }
    if (value === 'offline' && productType.value !== 'offline') {
      productType.value = 'offline';
    } else if (value === 'offline' && productType.value === 'offline') {
      productType.value = '';
    }
    if (value === 'unlocked' && availability.value !== 'unlocked') {
      availability.value = 'unlocked';
    } else if (value === 'unlocked' && availability.value === 'unlocked') {
      availability.value = '';
    }
    if (value === 'locked' && availability.value !== 'locked') {
      availability.value = 'locked';
    } else if (value === 'locked' && availability.value === 'locked') {
      availability.value = '';
    }
  };

  const emit = defineEmits(['applyFilters']);
  const resetFilters = () => {
    pointsRange.value = { min: 0, max: 10000 };
    productType.value = '';
    availability.value = '';
    locationString.value = '';
    selectedCategories.value = [];
    selectedValues.value = [];
    doNotShowOutOfStock.value = false;

    // Reset the values and categories to their initial state
    values.value = initialValues.value;
    categories.value = initialCategories.value;

    emit('applyFilters', {
      pointsRange,
      productType,
      availability,
      locationString,
      selectedCategories,
      selectedValues,
      doNotShowOutOfStock
    });
  };
</script>
